// Platform-agnostic program-editor model + transforms.
//
// The program editor is a shared react-native component (@heraion/ui);
// a DOM twin lived in the legacy web (removed with RW-14, readable at 7ae8e35e). This module
// holds the matrix draft model and the pivot / validation logic so any
// renderer agrees and the behaviour is
// unit-testable without a DOM or a native runtime. Keep it free of React
// and any UI import.
//
// Phase 5.2.2 — per-week per-set matrix.
//
// Each slot is a matrix: rows are set positions, columns are the cycle's
// weeks. A round may stop before the last row (#336 S1, CellDraft.absent). Each cell carries reps + an intensity
// value; the kind of intensity is per-row (set position), so set 1 can
// be "% of MAX" while set 2 is "RPE" if the coach wants. Within a row,
// only the value changes across weeks, which is the common case (top
// set ramps 75/80/85/90; back-off sits at 65 the whole cycle).

import {
  type AmrapStop,
  type GroupCadence as GroupCadenceT,
  type ProgramFocus,
  type ProgramStatus as ProgramStatusT,
  type RoundType as RoundTypeT,
  type SetIntensity as SetIntensityT,
  type SetWork as SetWorkT,
  type Variant as VariantT,
} from "./schemas/program.js";
import type { ProgramOrigin as ProgramOriginT } from "./schemas/programOrigin.js";
import type { AuthoredText as AuthoredTextT } from "./authoredText.js";
import type { Locale } from "./locale.js";
import type { Side } from "./side.js";
import { uuidv4 } from "./uuid.js";
import { blankCell, blankSetRow } from "./programEditSeedRow.js";

export type SlotGroupDraft = {
  id: string;
  kind: "superset" | "circuit";
  // The group's label, round-tripped like cadence below. Optional: absent
  // means the client renders its own default ("Superset N"), which is what
  // every program written before PE-30 carries.
  name?: string;
  // Stored cadence, carried verbatim (HI-1). The editor cannot author it
  // yet — it lands with the circuit slice — but this draft is rebuilt
  // field-by-field on save, so WITHOUT this the first edit of a grouped
  // slot would silently wipe a cadence the coach set elsewhere. Same
  // lossless-round-trip rule as variantsRaw / notesRawPerCell.
  cadence?: GroupCadenceT;
};

export type IntensityKind = SetIntensityT["kind"];

// Form state mirrors the matrix shape but stores everything as strings
// so the input fields don't fight with intermediate edits ("8.", "0.5",
// empty during typing). Conversion to numbers happens in buildPayload.
export type CellDraft = {
  reps: string;
  // PE-42 — AMRAP is a FIELD, not a character. It used to ride as a trailing
  // "+" on `reps`, which meant the only way to prescribe one was to know the
  // syntax; the chip is the control now. The stored schema always had a real
  // boolean (SetPrescription.amrap), so this removes a boolean→text→boolean
  // round trip rather than adding a carrier.
  // ⚠️ Readers still TOLERATE a "+" in `reps` (parseRepsToken): a draft
  // recovered at boot can have been written by the old code, and dropping the
  // tolerance would eat its AMRAP in silence rather than fail loudly.
  amrap?: boolean;
  // #355: where the AMRAP stops. Read only while the cell is an AMRAP; kept
  // when the "+" goes, like the range, so it comes back with it.
  amrapStop?: AmrapStop;
  intensityValue: string; // unused when kind = "none"
  // #336 S3: the rep range around `reps`, typed ends ("8", "12"). Both or
  // neither; ignored on an AMRAP or timed set (programRepsRange.ts).
  repsMin?: string;
  repsMax?: string;
  // #336: this cell's own axis, set only when it differs from the row's
  // (a kg row with RIR 3 in W4). Absent = the row's. Read it through kindAt.
  kind?: IntensityKind;
  // #336 S1: this set is not in this round, which has fewer sets than the
  // row count. Only ever the round's LAST rows. Read it through isCellAbsent.
  absent?: true;
  // Time axis (HI-1), strings like every other cell field so the inputs
  // bind directly. Both absent on a reps set, which is every set written
  // before this delta. workDurationSec is read only when the ROW's
  // workKind is "countdown" — a countup bout has no prescribed length.
  workDurationSec?: string;
  restSec?: string;
};

export type SetRowDraft = {
  intensityKind: IntensityKind;
  // How this set is BOUNDED, on the ROW exactly like intensityKind is:
  // the shape of the set is a property of the set, its numbers vary by
  // week. Absent = a reps set (today's only case).
  workKind?: SetWorkT["kind"];
  cells: CellDraft[]; // length = cycleWeeks
  notesPerCell: string[]; // length = cycleWeeks; rarely populated
  // PE-21 — the stored value of each cell's note, kept verbatim so an
  // edit round-trip never destroys a translation the editor isn't
  // showing. Same lossless-round-trip rule as `variantsRaw` below.
  //
  // OPTIONAL, and every *Raw companion below follows the same rule:
  // absent means "nothing stored behind this string", which is exactly
  // what a freshly created row, a plan, or an expanded block has. The
  // build path then emits a naked string — the pre-PE-21 behaviour, and
  // the one that can never destroy a translation, because there is none.
  notesRawPerCell?: (AuthoredTextT | undefined)[];
};

export type SlotDraft = {
  id: string;
  exerciseId: string;
  exerciseName: string | undefined;
  setRows: SetRowDraft[];
  instructions: string;
  // Stored `instructions` verbatim — see notesRawPerCell.
  instructionsRaw?: AuthoredTextT;
  amrapLastSet: boolean;
  maxAttempt: boolean;
  // Propagated onto the resolved PlannedExerciseEntry.rpeEnabled by
  // programResolution so the Logger opens with the RPE column on for
  // this slot (Logger redesign §6.3).
  rpeEnabled: boolean;
  // Same propagation as rpeEnabled (Phase 8). Carried so an edit
  // round-trip never silently clears it.
  rirEnabled: boolean;
  group: SlotGroupDraft | null;
  // The stored slot's full variants array, preserved verbatim so an
  // edit round-trip never destroys multi-variant cadence the editor
  // can't express yet (lossless-round-trip rule,
  // next/program-editor-redesign.md §3.2). The editor only reads/writes
  // exerciseId/exerciseName; buildPayload grafts those onto variants[0]
  // and re-emits the rest untouched. Empty on freshly created slots.
  variantsRaw: VariantT[];
  // inactiveWeeks[w] = true → this slot doesn't run in week w (stored
  // as weeks[w].sets = []). Length = cycleWeeks. The matrix UI doesn't
  // expose it; the scheduler-composer drives it via block week chips.
  inactiveWeeks: boolean[];
  // #356: Slot.rowId, carried as it is.
  rowId?: string;
  // #75: Slot.side, carried as it is. Absent: no side prescribed.
  side?: Side;
};

export type SessionDraft = {
  id: string;
  name: string;
  notes: string;
  // Stored values verbatim — see SetRowDraft.notesRawPerCell.
  nameRaw?: AuthoredTextT;
  notesRaw?: AuthoredTextT;
  slots: SlotDraft[];
};

export type Draft = {
  name: string;
  description: string;
  // Stored values verbatim — see SetRowDraft.notesRawPerCell.
  nameRaw?: AuthoredTextT;
  descriptionRaw?: AuthoredTextT;
  // PE-21 — the language the editor is WRITING. On an existing program
  // it is the language the coach authored it in, so opening a
  // translated program puts them back on their own words rather than on
  // a translation of them. buildPayload grafts every edited string into
  // THIS language of the stored value, leaving the others alone.
  //
  // `null` on a Plan draft, which shares this type but has no localised
  // fields at all: with every *Raw companion absent, the build path
  // emits naked strings and the language is genuinely inert. Saying so
  // beats stamping a default language a plan never chose. Absent is
  // read the same way: no writing language, so naked strings out.
  editLocale?: Locale | null;
  // The language the program DECLARES it was written in — what gets
  // saved as `Program.authorLocale`.
  //
  // ⛔ Deliberately NOT the same field as `editLocale`. Looking at the
  // French tab is not a claim that the program is French: if the two
  // were one field, a coach who tapped through to check a translation
  // and then saved would silently reassign the program's canonical
  // language, and that language is the tie-breaker every reader in the
  // app falls back on. It changes only when somebody declares it.
  declaredAuthorLocale?: Locale;
  status: ProgramStatusT;
  cycleWeeks: number;
  sessionsPerWeek: number;
  // #336 S2: one type per round, index-parallel to the rounds
  // (programRoundTypes.ts). Absent: all normal.
  roundTypes?: RoundTypeT[];
  // #356: Program.focus and Program.origin, carried as they are.
  focus?: ProgramFocus;
  origin?: ProgramOriginT;
  sessions: SessionDraft[];
  // Existing assignee subs preserved across PATCH (otherwise the
  // server's "assignedTo omitted → [callerSub]" default would silently
  // drop every athlete the coach already shared the program with).
  // On create this stays empty and the server fills [callerSub].
  assignedTo: string[];
  // New athlete emails the coach is adding this round. PlansCard
  // pattern: parallel field to assignedTo, server resolves through
  // Cognito + the edge-based permission gate. Empty when no new
  // additions.
  assigneeEmails: string[];
};

// PE-21 — a stored value only survives a text change when the coach is
// EDITING that text in their own language. When a text is REPLACED
// wholesale (a block applied over an existing slot, a member's cue
// written across weeks), the other languages describe the OLD text and
// become lies: an Italian cue replaced by "Esplosivo" would keep a
// French translation still reading "descends lentement". So the stored
// value is dropped exactly when the text actually changed, and kept
// when the write is a no-op.
export const rawForReplacedText = <T>(
  previousText: string,
  nextText: string,
  raw: T | undefined,
): T | undefined => (previousText === nextText ? raw : undefined);

// blankCell / blankSetRow live in ./programEditSeedRow (cycle-free runtime
// module, #283); re-exported so this file's public API is unchanged.
export { blankCell, blankSetRow };

// PE-47 — the id is minted HERE, on the client, not left empty for the
// server to stamp: two slots added in one editing session both read ""
// and the editor resolves the cursor onto the first one, so the
// second exercise never opens. ⛔ Not `crypto.randomUUID` (absent on
// Hermes). The saved document now carries client-minted slot ids;
// documents written before this keep the server's, and both are UUIDs.
export const blankSlot = (cycleWeeks: number): SlotDraft => ({
  id: uuidv4(),
  exerciseId: "",
  exerciseName: undefined,
  setRows: [blankSetRow(cycleWeeks)],
  instructions: "",
  amrapLastSet: false,
  maxAttempt: false,
  rpeEnabled: false,
  rirEnabled: false,
  group: null,
  variantsRaw: [],
  inactiveWeeks: Array.from({ length: cycleWeeks }, () => false),
});

// How many weeks this slot actually runs. Counted the way every READER of
// `inactiveWeeks` decides (`!slot.inactiveWeeks[w]`, e.g. slotDraftToEntry and
// prescribeDispatch's activeSlotIdxsAt), and bounded by `cycleWeeks` rather
// than by the array's own length: a short/absent array means "active", so
// counting its entries would under-report — and the first caller (PE-44's
// remove confirm) turns this number into a promise about what a tap destroys.
export const activeWeekCount = (slot: SlotDraft, cycleWeeks: number): number => {
  let n = 0;
  for (let w = 0; w < cycleWeeks; w += 1) if (!slot.inactiveWeeks[w]) n += 1;
  return n;
};

// Cell parsing + payload build/validation live in ./programEditBuild to
// keep this file under the 500-line cap. Re-exported here so the existing
// ./programEdit import paths (planEdit, tests, the shared index) keep
// working unchanged.
export {
  parseRepsToken,
  amrapOf,
  isLoadBearingKind,
  axisHasLoadValue,
  parseFloatLoose,
  buildIntensity,
  intensityToCell,
  kindAt,
  isCellAbsent,
  VALUELESS_KINDS,
  seedForIntensityKind,
} from "./programEditBuild.js";
export { programToDraft, newProgramToProgram, blankProgramSeed } from "./programEditRead.js";
export { buildPayload } from "./programEditPayload.js";
export type { BuildResult } from "./programEditPayload.js";
export {
  amrapWithTimeError,
  buildWorkForCell,
  timeAxisError,
} from "./programEditTimeAxis.js";
export type {
  IntensityErrorCode,
  BuildReasonCode,
} from "./programEditBuild.js";
